import { Button, InlineValuePopover, Input, Select, Switch } from "@xola/ui-kit";
import clsx from "clsx";
import { useFormikContext } from "formik";
import { isEmpty, isNil } from "lodash";
import pluralize from "pluralize";
import React, { ChangeEvent, useMemo, useState } from "react";
import { convertToMinutes, Duration, humanizeDuration, OffsetDurationUnit } from "../../../helpers/durationHelper";
import { ContentLayout } from "../../Layout/ContentLayout";
import { Loader } from "../../Loader";
import { ExperienceResourceSettingValues } from "./ExperienceResourceTypes";

const sectionClassNames = { content: "lg:!max-w-full" };

export const ExperienceGuideFields = () => {
    const { values, setFieldValue } = useFormikContext<ExperienceResourceSettingValues>();
    const [referGuideAs, setReferGuideAs] = useState(values.guidePreference?.referGuideAs ?? "Guides");
    const [showPicker, setShowPicker] = useState(false);
    const [showOffsetPicker, setShowOffsetPicker] = useState(false);

    const offset = values.guidePreference?.guideNotAssigned?.offset;
    const humanizedOffset = useMemo(() => {
        return isNil(offset) ? { duration: null, durationUnit: OffsetDurationUnit.HOURS } : humanizeDuration(offset);
    }, [offset]);

    const [notificationOffset, setNotificationOffset] = useState<Duration>(humanizedOffset);

    if (!values) {
        return <Loader />;
    }

    return (
        <ContentLayout.Section classNames={sectionClassNames} title="Guides">
            <div className="space-y-6">
                <div className="flex items-start space-x-2">
                    <Switch
                        data-testid="experience-guide-fields-resource-guide-information-tab"
                        size="medium"
                        isChecked={values.guidePreference?.includeGuideInfo ?? false}
                        onChange={(value: boolean) => {
                            setFieldValue("guidePreference.includeGuideInfo", value);
                            setFieldValue("guidePreference.referGuideAs", value ? referGuideAs : "");
                        }}
                    />
                    <div>
                        <h5 className="font-bold">Guide Information</h5>
                        <span className="text-gray-darker">
                            Display assigned guide information in customer communications.
                        </span>
                        {values.guidePreference?.includeGuideInfo ? (
                            <div className="flex space-x-1">
                                <div className="text-gray-darker">Refer to guides in customer communications as</div>
                                <InlineValuePopover
                                    showArrow
                                    text={
                                        <span className="no-translate border-b border-black">
                                            {values.guidePreference.referGuideAs ?? "Guides"}
                                        </span>
                                    }
                                    isOpen={showPicker}
                                    placement="bottom"
                                    classNames={{ text: "text-black mx-1" }}
                                    onClick={() => setShowPicker(true)}
                                    onClickOutside={() => {
                                        setShowPicker(false);
                                        setReferGuideAs(values.guidePreference?.referGuideAs ?? "");
                                    }}
                                >
                                    <div className="flex space-x-2 p-0">
                                        <Input
                                            placeholder={referGuideAs}
                                            value={referGuideAs}
                                            className={clsx(isEmpty(referGuideAs) && "border-danger", null)}
                                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                                setReferGuideAs(e.target.value);
                                            }}
                                        />
                                        <Button
                                            disabled={isEmpty(referGuideAs)}
                                            onClick={() => {
                                                setFieldValue("guidePreference.referGuideAs", referGuideAs);
                                                setShowPicker(false);
                                            }}
                                        >
                                            Apply
                                        </Button>
                                    </div>
                                    {isEmpty(referGuideAs) ? (
                                        <span className="text-sm text-danger">Required</span>
                                    ) : null}
                                </InlineValuePopover>
                            </div>
                        ) : null}
                    </div>
                </div>

                <div className="flex items-start space-x-2">
                    <Switch
                        data-testid="experience-guide-fields-resource-unassigned-event-tab"
                        size="medium"
                        isChecked={values.guidePreference?.guideNotAssigned?.enabled}
                        onChange={(value: boolean) => {
                            setFieldValue("guidePreference.guideNotAssigned.enabled", value);
                        }}
                    />
                    <div>
                        <h5 className="font-bold">Unassigned Event Alert</h5>
                        {values.guidePreference?.guideNotAssigned?.enabled ? (
                            <div className="max-w-[500px]">
                                <span className="mr-1 text-gray-darker">
                                    Send me an email if there is no guide assigned to an event with confirmed
                                    reservations
                                </span>
                                <InlineValuePopover
                                    showArrow
                                    text={
                                        <span className="border-b border-black">
                                            <span className="no-translate">
                                                {isNil(humanizedOffset.duration) ? "__" : humanizedOffset.duration}
                                            </span>
                                            &nbsp;
                                            <span>
                                                {pluralize(humanizedOffset.durationUnit, humanizedOffset.duration ?? 0)}
                                            </span>
                                        </span>
                                    }
                                    isOpen={showOffsetPicker}
                                    placement="bottom"
                                    classNames={{ text: "text-black mx-1" }}
                                    onClick={() => {
                                        setShowOffsetPicker(true);
                                    }}
                                    onClickOutside={() => {
                                        setNotificationOffset(humanizedOffset);
                                        setShowOffsetPicker(false);
                                    }}
                                >
                                    <div className="flex space-x-2 p-0">
                                        <Input
                                            className="w-20"
                                            type="number"
                                            min="0"
                                            step="1"
                                            value={notificationOffset.duration ? notificationOffset.duration : null}
                                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                                setNotificationOffset({
                                                    ...notificationOffset,
                                                    duration: e.target.value
                                                        ? Number.parseInt(e.target.value, 10)
                                                        : null,
                                                });
                                            }}
                                        />
                                        <Select
                                            value={notificationOffset.durationUnit ?? OffsetDurationUnit.HOURS}
                                            className="h-full w-32"
                                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                                setNotificationOffset({
                                                    ...notificationOffset,
                                                    durationUnit: e.target.value as OffsetDurationUnit,
                                                });
                                            }}
                                        >
                                            <option value={OffsetDurationUnit.DAYS}>
                                                {pluralize(OffsetDurationUnit.DAYS, 2)}
                                            </option>
                                            <option value={OffsetDurationUnit.HOURS}>
                                                {pluralize(OffsetDurationUnit.HOURS, 2)}
                                            </option>
                                        </Select>

                                        <Button
                                            onClick={() => {
                                                const offset = isNil(notificationOffset.duration)
                                                    ? null
                                                    : convertToMinutes(
                                                          notificationOffset.duration ?? 0,
                                                          notificationOffset.durationUnit,
                                                      );
                                                setFieldValue("guidePreference.guideNotAssigned.offset", offset);
                                                setShowOffsetPicker(false);
                                            }}
                                        >
                                            Apply
                                        </Button>
                                    </div>
                                </InlineValuePopover>
                                <span className="text-gray-darker">in advance</span>
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        </ContentLayout.Section>
    );
};
